import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { adApi } from '../api/ad.api';
import MiniBanner from '../components/ad/MiniBanner';
import { weightedRandomSelect } from '../util/weightedRandom';
import FingerPrintJS from '@fingerprintjs/fingerprintjs';
import toast from 'react-hot-toast';
import { Star } from 'lucide-react';
import { questionApi } from '../api/question.api';
import { QuestionDetailResponse, QuestionFavoriteRequest, VoteQuestionRequest } from '../types/Question.api.type';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardFooter, CardHeader } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { formatTimeAgo, remainingQuestionTime } from '../util/DateUtil';
import {
  BarChartIcon,
  ClockIcon,
  CopyIcon,
  EditIcon,
  FlagIcon,
  InfoIcon,
  KakaoIcon,
  MoreHorizontalIcon,
  ShareIcon,
  TrashIcon,
  UserIcon,
} from '../components/ui/icons';
import { questionInitializer } from '../types/Question.initializer';
import LoginRequireModal from '../components/modal/LoginRequireModal';
import ReportModal from '../components/modal/ReportModal';
import { useAuth } from '../context/AuthContext';
import { userApi } from '../api/user.api';
import { GetUserDashboardProfileResponse } from '../types/User.api.type';
import { GridSkeleton, SectionLoading } from '../components/ui/skeleton';
import CommentSection from '../components/CommentSection';
import UserReactions from '../components/UserReactions';
import { reactionApi } from '../api/reaction.api';
import { UserReactionRequest } from '../types/Reaction.api.type';
import { reactionInitializer } from '../types/Reaction.initializer';
import { ReactionType } from '../types/Reaction.type';
import logo from '../img/onlymelogo.png';
import { useOnClickOutside } from '../hooks/useOnClickOutside';
import { CreateReportRequest } from '../types/Report.api.type';
import { reportApi } from '../api/report.api';
import { reportInitializer } from '../types/Report.initializer';
import { VoteAnalysisResponse, VotingProfile, AiAnalysisResponse } from '../types/Vote.api.type';
import { VoteAnalysisSection } from '../components/VoteAnalysisSection';
import { userRewardApi } from '../api/reward.api';
import type { VoteRewardResponse, AdFreeStatusResponse } from '../types/Reward.api.type';

const QuestionDetail = () => {
  /**
   * Hooks
   */
  /* navigator */
  const navigate = useNavigate();
  /* 로그인 여부 */
  const { isAuthenticated, user } = useAuth();
  const queryClient = useQueryClient();
  const moreMenuRef = useRef<HTMLDivElement>(null);
  const shareMenuRef = useRef<HTMLDivElement>(null);

  /**
   * States
   */
  /* 공유하기 팝업 오픈 제어 state */
  const [isShareMenuOpen, setIsShareMenuOpen] = useState<boolean>(false);
  /* 수정삭제 더보기 메뉴 팝업 오픈 제어 state */
  const [isMoreMenuOpen, setIsMoreMenuOpen] = useState<boolean>(false);
  /* fingerprint(익명) */
  const [fingerprint, setFingerprint] = useState<string | null>(null);
  /* 연계설문 step 제어: null=비활성, 'gender'=성별, 'ageGroup'=연령대 */
  const [surveyStep, setSurveyStep] = useState<'gender' | 'ageGroup' | null>(null);
  const [surveyGender, setSurveyGender] = useState<string | null>(null);
  /* 로그인 필요 팝업 오픈 제어 state */
  const [isLoginRequireOpen, setIsLoginRequireOpen] = useState<boolean>(false);
  /* 설문옵션 선택시 옵션번호 세팅 */
  const [selectedQuestionOptionId, setSelectedQuestionOptionId] = useState<string | null>(null);
  /* 설문 삭제 모달 오픈 제어 state */
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState<boolean>(false);
  /* 유저 리액션 파라미터 state */
  const [userReactionParam, setUserReactionParam] = useState<UserReactionRequest>(
    reactionInitializer.INITIAL_USER_REACTION_PARAM,
  );
  /* 분석 표시 여부 (광고 게이트) */
  const [showAnalysis, setShowAnalysis] = useState<boolean>(false);
  /**/
  const [isReportOpen, setIsReportOpen] = useState<boolean>(false);
  const [isReportSubmitted, setIsReportSubmitted] = useState<boolean>(false);
  const [createReportParam, setCreateReportParam] = useState<CreateReportRequest>(
    reportInitializer.INITIAL_CREATE_REPORT_PARAM,
  );

  /**
   * Variables
   */
  const { questionId } = useParams<{ questionId: string }>();

  /* 설문 즐겨찾기 파라미터 state */
  const [questionFavoriteParam, setQuestionFavoriteParam] = useState<QuestionFavoriteRequest>({
    questionId,
  });

  /**
   * APIs
   */
  /* 설문 상세정보 조회 api */
  const { data: questionDetail, isFetching } = useQuery<QuestionDetailResponse>({
    queryKey: isAuthenticated ? ['questionDetail', questionId] : ['questionDetail', questionId, fingerprint],
    initialData: questionInitializer.INITIAL_QUESTION_DETAIL,
    queryFn: () => {
      console.log(
        '[QuestionDetail] 데이터 가져오기 시작. queryKey:',
        JSON.stringify(isAuthenticated ? ['questionDetail', questionId] : ['questionDetail', questionId, fingerprint]),
      );
      return questionApi.getQuestionDetail(questionId!, fingerprint).then((result) => {
        return result.data.results;
      });
    },
    enabled: !!questionId && (isAuthenticated || !!fingerprint),
    meta: { hideGlobalLoader: false },
  });
  /* 유저 프로필 조회 (연계설문 판단용) */
  const { data: profileData } = useQuery<GetUserDashboardProfileResponse>({
    queryKey: ['getDashboardProfile'],
    queryFn: () => userApi.getDashboardProfile().then((res) => res.data),
    enabled: isAuthenticated,
  });

  /** 상세 댓글 위 광고 조회 */
  const { data: detailAdsData } = useQuery({
    queryKey: ['ads', 'DETAIL_COMMENT'],
    queryFn: () => adApi.getAdsByPosition('DETAIL_COMMENT').then(res => res.data),
    staleTime: 0,
  });
  const detailAd = useMemo(() => {
    if (!detailAdsData || detailAdsData.length === 0) return undefined;
    return weightedRandomSelect(detailAdsData);
  }, [detailAdsData]);

  /* 설문 총 투표수 계산 */
  const totalVotes = useMemo(
    () => questionDetail.questionOptions.reduce((sum, option) => sum + option.voteCount, 0),
    [questionDetail],
  );
  /* 광고 제거 상태 조회 (로그인 + 투표/종료 + 5표 이상일 때만) */
  const canShowAnalysisArea = (questionDetail.hasVoted || questionDetail.isExpired) && totalVotes >= 5;
  const { data: adFreeStatus, isError: isAdFreeError } = useQuery<AdFreeStatusResponse>({
    queryKey: ['adFreeStatus', questionId],
    queryFn: () =>
      userRewardApi.getAdFreeStatus(Number(questionId)).then((res) => res.data.results),
    enabled: isAuthenticated && canShowAnalysisArea && !!questionId,
    staleTime: 30_000,
  });
  /* S6-23: 정책 기반 무료 분석 판별 */
  const policy = adFreeStatus?.analysisPolicy ?? 'AD_THEN_PERMANENT';
  const isFirstView = !adFreeStatus?.questionUnlocked;
  const canViewFree = (() => {
    if (policy === 'ALWAYS_FREE') return true;
    if (policy === 'FIRST_FREE_THEN_AD') return !isAuthenticated || adFreeStatus?.isAdFree || isFirstView || isAdFreeError;
    return !isAuthenticated || adFreeStatus?.isAdFree || adFreeStatus?.questionUnlocked || isAdFreeError;
  })();
  /* 설문 투표 분석 API (S6-23: BE가 인증 토큰에서 userId 추출) */
  const { data: analysisData } = useQuery<VoteAnalysisResponse>({
    queryKey: ['voteAnalysis', questionId],
    queryFn: () => questionApi.getVoteAnalysis(questionId!),
    enabled: showAnalysis && !!questionId,
    staleTime: Infinity,
  });
  /* 투표 성향 프로필 (S6-23) */
  const { data: votingProfileData } = useQuery<VotingProfile>({
    queryKey: ['votingProfile'],
    queryFn: () => questionApi.getVotingProfile(20),
    enabled: showAnalysis && isAuthenticated,
    staleTime: 5 * 60 * 1000,
  });
  /* AI 분석 (S6-23) */
  const { data: aiData, isLoading: isAiLoading } = useQuery<AiAnalysisResponse>({
    queryKey: ['aiAnalysis', questionId],
    queryFn: () => questionApi.getAiAnalysis(Number(questionId)),
    enabled: showAnalysis && isAuthenticated && !!questionId && (adFreeStatus?.isAiEnabled ?? false),
    staleTime: Infinity,
  });
  /* 분석 해제 mutation */
  const { mutate: unlockAnalysisMutation, isPending: isUnlocking } = useMutation({
    mutationFn: () => userRewardApi.unlockAnalysis(Number(questionId)),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['adFreeStatus'] });
      setShowAnalysis(true);
    },
    onError: () => {
      toast.error('분석 해제에 실패했습니다.');
    },
  });
  /* 설문 투표 api */
  const { mutate: voteQuestion } = useMutation({
    mutationFn: (params: {
      questionOptionId: string;
      voterGender?: string | null;
      voterAgeGroup?: string | null;
    }) => {
      const voteQuestionParam: VoteQuestionRequest = {
        questionId: questionDetail.questionId,
        questionOptionId: params.questionOptionId,
        voterGender: params.voterGender,
        voterAgeGroup: params.voterAgeGroup,
      };
      // 로그인하지 않은 경우 익명(fingerprint)
      if (!isAuthenticated) {
        if (!fingerprint) {
          throw new Error('Fingerprint is not available for anonymous user.');
        }
        voteQuestionParam.fingerprint = fingerprint;
      }
      return questionApi.voteQuestion(voteQuestionParam);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['questionDetail', questionId] });
      // 로그인 사용자에게만 리워드 지급 시도
      if (isAuthenticated && questionId) {
        userRewardApi.claimVoteReward(Number(questionId))
          .then((res) => {
            const data = res.data.results as VoteRewardResponse;
            if (data.rewarded) {
              toast.success(data.message || '리워드를 받았어요!');
            } else if (data.message) {
              toast.success(data.message);
            } else {
              toast.success('투표에 성공했어요.');
            }
            // S6-14: dailyCount 기반 진행률 토스트
            const { dailyCount } = data;
            if (dailyCount === 3 || dailyCount === 7) {
              setTimeout(() => toast.success('보너스를 받을 수 있어요!'), 1500);
            } else if (dailyCount < 3) {
              setTimeout(() => toast.success(`${3 - dailyCount}회 더 투표하면 보너스!`), 1500);
            } else if (dailyCount < 7) {
              setTimeout(() => toast.success(`${7 - dailyCount}회 더 투표하면 추가 보너스!`), 1500);
            }
          })
          .catch((err) => {
            console.error('[RewardAPI] 투표 리워드 실패:', err?.response?.status, err?.response?.data);
            toast.success('투표에 성공했어요.');
          });
      } else {
        toast.success('투표에 성공했어요.');
      }
    },
    onError: (error) => {
      console.log(error);
    },
  });
  /* 설문 삭제 api */
  const { mutate: deleteQuestionMutation } = useMutation({
    mutationFn: () => {
      return questionApi.deleteQuestion(questionId!);
    },
    onSuccess: () => {
      // 삭제 성공 시, 목록 페이지로 이동
      console.log('설문이 삭제되었습니다.');
      navigate('/');
    },
    onError: (error) => {
      console.error('설문 삭제 실패:', error);
    },
  });

  /* 유저 리액션 api */
  const { mutate: userReactionMutation } = useMutation({
    mutationFn: (reaction: ReactionType) => {
      console.log('param', userReactionParam);
      console.log('reactiton', reaction);
      setUserReactionParam({
        questionId: questionId!,
        reactionType: reaction,
      });
      const param: UserReactionRequest = {
        ...userReactionParam,
        questionId: questionId!,
        reactionType: reaction,
        fingerprint: !isAuthenticated ? fingerprint! : undefined, // 비회원인 경우에만 fingerprint 사용
      };
      return reactionApi.userReaction(questionId!, param);
    },
    onMutate: async (reaction: ReactionType) => {
      // 진행중인 쿼리 취소
      const queryKey = isAuthenticated ? ['questionDetail', questionId] : ['questionDetail', questionId, fingerprint];
      await queryClient.cancelQueries({ queryKey });

      // 이전 값 저장
      const previousData = queryClient.getQueryData<QuestionDetailResponse>(queryKey);

      // UI Update
      if (previousData) {
        queryClient.setQueryData(queryKey, (oldData: QuestionDetailResponse | undefined) => {
          if (!oldData) return undefined;

          const currentReaction = oldData.questionReaction.userReaction;
          let newLikeCount = oldData.questionReaction.likeCount || 0;
          let newDislikeCount = oldData.questionReaction.dislikeCount || 0;
          let newUserReaction: string | null = reaction;
          // 이전 리액션 취소 처리
          if (currentReaction === 'LIKE') {
            newLikeCount -= 1;
          } else if (currentReaction === 'DISLIKE') {
            newDislikeCount -= 1;
          }
          // 새 리액션 적용
          if (reaction === 'LIKE') {
            if (currentReaction === 'LIKE') {
              // 좋아요 버튼 재클릭 → 취소
              newUserReaction = null;
            } else {
              // 새로 좋아요 추가
              newLikeCount += 1;
            }
          } else if (reaction === 'DISLIKE') {
            if (currentReaction === 'DISLIKE') {
              // 싫어요 버튼 재클릭 → 취소
              newUserReaction = null;
            } else {
              // 새로 싫어요 추가
              newDislikeCount += 1;
            }
          }

          return {
            ...oldData,
            questionReaction: {
              ...oldData.questionReaction,
              userReaction: newUserReaction,
              likeCount: newLikeCount,
              dislikeCount: newDislikeCount,
            },
          };
        });
      }
    },
  });
  /* 즐겨찾기 api */
  const { mutate: questionFavoriteMutation } = useMutation({
    mutationFn: () => {
      setQuestionFavoriteParam({
        questionId,
      });
      console.log('param', questionFavoriteParam);
      return questionApi.questionFavorite(questionFavoriteParam);
    },
    onSuccess: (response) => {
      const newIsFavorited = response.data.results.isFavorited;
      // 인증 여부에 따라, queryKey제어
      const queryKey = isAuthenticated ? ['questionDetail', questionId] : ['questionDetail', questionId, fingerprint];
      queryClient.setQueryData(queryKey, (oldData: QuestionDetailResponse | undefined) => {
        if (oldData) {
          return {
            ...oldData,
            isFavorited: newIsFavorited,
          };
        }
        return undefined;
      });
      toast.success(newIsFavorited ? '즐겨찾기에 추가되었습니다.' : '즐겨찾기에 해제되었습니다.');
      // if (questionId) {
      //   queryClient.invalidateQueries({ queryKey: ['questionDetail', questionId] });
      // }
    },
    onError: (error) => {
      console.error('<UNK> <UNK> <UNK>:', error);
    },
  });
  /* 신고하기 API */
  const { mutate: createReportMutation, isPending: isReportSubmitting } = useMutation({
    mutationFn: (params: CreateReportRequest) => reportApi.createReport(params),
    onSuccess: () => {
      setIsReportSubmitted(true);
      toast.success('신고가 정상적으로 접수되었습니다.');
      // setIsReportOpen(false);
    },
    onError: (error) => {
      toast.error('신고 접수에 실패하였습니다.');
    },
  });

  /**
   * Handlers
   */
  const handleGoBack = () => {
    navigate('/');
    // if (returnUrl) {
    //   // return URL이 있으면 해당 URL로 이동하면서 focus 파라미터 추가
    //   const url = new URL(returnUrl, window.location.origin);
    //   if (focusId) {
    //     url.searchParams.set('focus', focusId);
    //   }
    //   navigate(url.pathname + url.search);
    // } else {
    //   // return URL이 없으면 단순히 뒤로가기
    //   navigate('/');
    // }
  };

  const handleVote = (questionOptionId: string, optionOrder: number) => {
    console.log('handleVote', questionOptionId);

    // 회원전용 설문인 경우, 회원이 아닌경우 로그인 필요 모달을 열어준다.
    if (questionDetail.memberOnly && !isAuthenticated) {
      setIsLoginRequireOpen(true);
      return;
    }
    setSelectedQuestionOptionId(questionOptionId);

    // 연계설문 필요 여부 판단
    const needGender = isAuthenticated ? !profileData?.gender : true;
    const needAgeGroup = isAuthenticated ? !profileData?.birthdate : true;

    if (needGender) {
      setSurveyStep('gender');
    } else if (needAgeGroup) {
      setSurveyStep('ageGroup');
    } else {
      // 정보가 모두 있으면 바로 투표
      voteQuestion({ questionOptionId });
    }
  };

  /* 연계설문: 성별 선택 */
  const handleSurveyGender = (value: string | null) => {
    setSurveyGender(value);
    const needAgeGroup = isAuthenticated ? !profileData?.birthdate : true;
    if (needAgeGroup) {
      setTimeout(() => setSurveyStep('ageGroup'), 150);
    } else {
      // 연령대 불필요 → 바로 투표
      completeSurveyAndVote(value, null);
    }
  };

  /* 연계설문: 연령대 선택 */
  const handleSurveyAgeGroup = (value: string | null) => {
    completeSurveyAndVote(surveyGender, value);
  };

  /* 연계설문 완료 → 투표 실행 */
  const completeSurveyAndVote = (voterGender: string | null, voterAgeGroup: string | null) => {
    if (selectedQuestionOptionId) {
      voteQuestion({
        questionOptionId: selectedQuestionOptionId,
        voterGender,
        voterAgeGroup,
      });
    }
    // 상태 초기화
    setSurveyStep(null);
    setSurveyGender(null);
  };

  /* 분석 보기 버튼 클릭 (S6-23) */
  const handleAnalysisClick = () => {
    if (canViewFree) {
      if (isAuthenticated && questionId && !isAdFreeError) {
        userRewardApi.analysisFreeView(Number(questionId)).catch(() => {});
      }
      setShowAnalysis(true);
    } else {
      const msg = policy === 'FIRST_FREE_THEN_AD'
        ? '[테스트] 광고를 시청하시겠습니까?\n확인 시 분석을 다시 볼 수 있어요.'
        : '[테스트] 광고를 시청하시겠습니까?\n확인 시 30분간 광고 없이 분석을 볼 수 있어요.';
      const confirmed = window.confirm(msg);
      if (confirmed) {
        unlockAnalysisMutation();
      }
    }
  };

  const shareToKakao = () => {
    if (window.Kakao) {
      if (!window.Kakao.isInitialized()) {
        toast.error('카카오톡 공유가 실패했습니다.');
        setIsShareMenuOpen(false);
        return;
      }
    }
    // 카카오톡 공유하기 파라미터 세팅
    const shareUrl = window.location.href;
    const title = questionDetail.questionTitle;
    const description = '지금 바로 참여해보세요!!';
    const imageUrl = logo;
    window.Kakao.Share.sendDefault({
      objectType: 'feed',
      content: {
        title,
        description,
        imageUrl,
        link: {
          mobileWebUrl: shareUrl,
          webUrl: shareUrl,
        },
      },
      buttons: [
        {
          title: '설문 참여하기',
          link: {
            mobileWebUrl: shareUrl,
            webUrl: shareUrl,
          },
        },
      ],
    });
    toast.success('카카오톡 공유가 성공하였습니다.');
    setIsShareMenuOpen(false);
  };
  /* 링크복사 기능 handler */
  const handleCopyLink = async () => {
    try {
      await navigator.clipboard.writeText(window.location.href);
      setIsShareMenuOpen(false);
      toast.success('링크가 복사되었습니다.');
    } catch (error) {
      console.log(error);
      toast.error('링크 복사에 실패하였습니다.');
    }
  };

  const onCloseLoginRequireModal = () => {
    setIsLoginRequireOpen(false);
  };
  /** 설문 삭제 handler */
  const handleDeleteQuestion = () => {
    deleteQuestionMutation();
  };
  /* 좋아요/싫어요 클릭 */
  const handleReaction = (reaction: ReactionType) => {
    console.log('handleReaction', reaction);
    userReactionMutation(reaction);
  };
  /* 신고하기 모달 제어 handler */
  const handleReportModal = async () => {
    // 로그인하지 않은 경우 로그인 필요 모달을 열어준다.
    if (!isAuthenticated) {
      setIsLoginRequireOpen(true);
      return;
    }

    try {
      const response = await reportApi.getIsReport(questionId!);
      console.log('report', response);
      if (response.data.results.isReport) {
        toast.error('이미 신고한 질문입니다.');
      } else {
        setIsReportOpen(true);
      }
    } catch (error) {
      console.error('신고 여부 확인 실패:', error);
      toast.error('신고 상태를 확인하는 중 오류가 발생했습니다.');
    }
  };
  /* 즐겨찾기 제어 handler */
  const handleFavorite = () => {
    questionFavoriteMutation();
  };
  const onCloseReportModal = () => {
    setIsReportOpen(false);
    setTimeout(() => {
      setIsReportSubmitted(false);
      setCreateReportParam(reportInitializer.INITIAL_CREATE_REPORT_PARAM);
    }, 300);
  };
  const handleReportParamChange = (key: 'reportReason' | 'details', value: string) => {
    setCreateReportParam((prev) => ({ ...prev, [key]: value }));
  };
  /* 신고하기 handler */
  const handleCreateReport = () => {
    console.log('handleCreateReport');
    if (!createReportParam.reportReason) {
      toast.error('신고 사유를 선택해주세요.');
      return;
    }
    if (createReportParam.reportReason === 'OTHER' && !createReportParam.details.trim()) {
      toast.error('기타 사유를 입력해주세요.');
      return;
    }
    createReportMutation({ ...createReportParam, questionId: questionId! });
  };
  useEffect(() => {
    const getFingerprint = async () => {
      const fp = await FingerPrintJS.load();
      const result = await fp.get();
      setFingerprint(result.visitorId);
    };
    if (!isAuthenticated) {
      getFingerprint();
    }
  }, [isAuthenticated]);

  // 외부 클릭시 닫히도록
  useOnClickOutside(moreMenuRef, () => setIsMoreMenuOpen(false));
  useOnClickOutside(shareMenuRef, () => setIsShareMenuOpen(false));

  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsMoreMenuOpen(false);
        setIsShareMenuOpen(false);
      }
    };

    if (isMoreMenuOpen || isShareMenuOpen) {
      document.addEventListener('keydown', handleEscape);
    }

    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isMoreMenuOpen, isShareMenuOpen]);

  return (
    <div>
      <div className="min-h-screen bg-gray-50">
        {/* 메인 콘텐츠 */}
        <div className="max-w-4xl mx-auto px-4 py-6">
          {/* 뒤로가기 버튼 */}
          <div className="mb-6">
            <Button
              variant="ghost"
              size="sm"
              className="flex items-center gap-2 text-muted-foreground hover:text-fore-ground"
              onClick={handleGoBack}>
              ← 목록으로 돌아가기
            </Button>
          </div>
          <SectionLoading
            isLoading={isFetching}
            skeleton={
              <GridSkeleton
                count={1}
                columns={{
                  default: 1,
                }}
              />
            }>
            <Card className={`${questionDetail.isExpired ? 'opacity-75 border-gray-300' : ''}`}>
              <CardHeader>
                <div className="space-y-4">
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex items-center gap-2 flex-wrap">
                      <Badge className="felx items-center gap-1 text-xs h-6">
                        <span>{questionDetail.categoryEmoji}</span>
                        <span>{questionDetail.categoryNameKr}</span>
                      </Badge>
                      {/* 설문 상태 배지 */}
                      {questionDetail.isExpired ? (
                        <Badge
                          variant="secondary"
                          className="flex items-center gap-1 text-xs bg-gray-100 text-gray-600">
                          <span>🏁</span>
                          <span>종료됨</span>
                        </Badge>
                      ) : (
                        <Badge
                          variant="outline"
                          className={`flex items-center gap-1 text-xs ${
                            remainingQuestionTime(questionDetail.endDate).includes('분') &&
                            !remainingQuestionTime(questionDetail.endDate).includes('시간') &&
                            !remainingQuestionTime(questionDetail.endDate).includes('일')
                              ? 'border-red-300 text-red-600 bg-red-50'
                              : 'border-green-300 text-green-600 bg-green-50'
                          }`}>
                          <ClockIcon className="w-3 h-3" />
                          <span>{remainingQuestionTime(questionDetail.endDate)}</span>
                        </Badge>
                      )}
                    </div>
                    {/* 카드 우측 상단 버튼 모음 */}
                    <div className="flex items-center gap-1">
                      {/* 즐겨찾기 버튼 */}
                      <div>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-10 w-10 p-0 text-muted-foreground hover:text-fore-ground"
                          onClick={() => handleFavorite()}>
                          <Star
                            className={`h-5 w-5 transition-colors ${
                              questionDetail.isFavorited ? 'text-yellow-400 fill-yellow-400' : 'text-muted-foreground'
                            }`}
                          />
                        </Button>
                      </div>
                      {/* 공유 버튼 */}
                      <div className="relative" ref={shareMenuRef}>
                        <Button
                          variant="ghost"
                          size="sm"
                          className="h-10 w-10 p-0 text-muted-foreground hover:text-fore-ground"
                          onClick={() => setIsShareMenuOpen(!isShareMenuOpen)}>
                          <ShareIcon className="w-4 h-4" />
                        </Button>
                        {/* 공유 팝업 메뉴 */}
                        {isShareMenuOpen && (
                          <div className="absolute right-0 top-full mt-1 w-56 bg-white border border-border rounded-lg shadow-xl z-50 animate-fade-in">
                            <div className="p-2">
                              <button
                                type="button"
                                onClick={shareToKakao}
                                className="w-full px-4 py-3 text-left text-sm hover:bg-accent flex items-center gap-3 rounded-lg transition-colors">
                                <div className="w-8 h-8 bg-yellow-400 rounded-lg flex items-center justify-center">
                                  <KakaoIcon className="w-5 h-5 text-black" />
                                </div>
                                <div className="flex-1">
                                  <div className="font-medium text-fore-ground">카카오톡으로 공유</div>
                                  <div className="text-xs text-muted-foreground">친구들에게 설문 공유하기</div>
                                </div>
                              </button>
                              <div className="h-px bg-border my-2" />
                              <button
                                type="button"
                                className="w-full px-4 py-3 text-left text-sm hover:bg-accent flex items-center gap-3 rounded-lg transition-colors"
                                onClick={handleCopyLink}>
                                <div className="w-8 h-8 bg-gray-100 rounded-lg flex items-center justify-center">
                                  <CopyIcon className="w-5 h-5 text-gray-600" />
                                </div>
                                <div className="flex-1">
                                  <div className="font-medium">링크 복사</div>
                                  <div className="text-xs text-muted-foreground">URL을 클립보드에 복사</div>
                                </div>
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                      {/* 신고 버튼 */}
                      <Button
                        type="button"
                        variant="ghost"
                        size="sm"
                        className="h-10 w-10 p-0 text-muted-foreground hover:text-fore-ground"
                        onClick={handleReportModal}>
                        <FlagIcon className="w-5 h-5" />
                      </Button>
                      {/* 수정/삭제 메뉴 (canEdit/canDelete 가드) */}
                      {(questionDetail.canEdit || questionDetail.canDelete) && (
                        <div className="relative" ref={moreMenuRef}>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="w-10 h-10 p-0 text-muted-foreground hover:text-fore-ground"
                            onClick={() => setIsMoreMenuOpen(!isMoreMenuOpen)}>
                            <MoreHorizontalIcon />
                          </Button>
                          {isMoreMenuOpen && (
                            <div className="absolute right-0 top-full mt-1 w-32 bg-white border border-border rounded-lg shadow-lg z-10">
                              {questionDetail.canEdit && (
                                <button
                                  type="button"
                                  className="w-full px-3 py-2 text-left text-sm hover:bg-accent flex items-center gap-2 rounded-t-lg"
                                  onClick={() => {
                                    setIsMoreMenuOpen(false);
                                    navigate(`/question/edit/${questionId}`);
                                  }}>
                                  <EditIcon />
                                  수정
                                </button>
                              )}
                              {questionDetail.canDelete && (
                                <button
                                  type="button"
                                  className="w-full px-3 py-2 text-left text-sm hover:bg-accent text-destructive flex items-center gap-2 rounded-b-lg"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setIsDeleteModalOpen(true);
                                    setIsMoreMenuOpen(false);
                                  }}>
                                  <TrashIcon />
                                  삭제
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                  {questionDetail.memberOnly && (
                    <div className="font-bold text-red-400">회원만 투표할 수 있는 설문입니다.</div>
                  )}
                  {/* 작성자 정보 */}
                  <div className="flex items-center gap-3">
                    <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center flex-shrink-0">
                      {questionDetail.profileImageUrl ? (
                        <img
                          src={questionDetail.profileImageUrl}
                          alt="profile"
                          className="w-10 h-10 rounded-full object-cover"
                        />
                      ) : (
                        <UserIcon className="w-5 h-5 text-primary" />
                      )}
                    </div>
                    <div className="flex flex-col">
                      <span className="font-medium text-fore-ground">{questionDetail.username}</span>
                      <span className="text-sm text-muted-foreground">{formatTimeAgo(questionDetail.regDateTime)}</span>
                    </div>
                  </div>
                  {/* 질문 제목 (연계설문 시 안내 문구로 전환) */}
                  <h2
                    className={`text-xl sm:text-2xl font-bold leading-relaxed ${questionDetail.isExpired ? 'text-gray-600' : 'text-fore-ground'}`}>
                    {surveyStep === 'gender'
                      ? '성별을 선택해주세요'
                      : surveyStep === 'ageGroup'
                        ? '연령대를 선택해주세요'
                        : questionDetail.questionTitle}
                  </h2>
                  {/* 질문 설명 (연계설문 시 안내 문구로 전환) */}
                  <div
                    className={`text-base ${questionDetail.isExpired ? 'text-gray-600' : 'text-muted-foreground'} leading-relaxed`}>
                    <div className="flex items-start gap-3">
                      <InfoIcon className="w-5 h-5 text-muted-foreground mt-0.5 flex-shrink-0" />
                      <div className="flex-1">
                        <p className="whitespace-pre-wrap leading-relaxed">
                          {surveyStep !== null
                            ? (
                              <>
                                정확한 분석을 위해 입력해주세요
                                {isAuthenticated && (
                                  <span className="block text-xs text-blue-500 mt-1">
                                    내 정보에서 나이/성별을 입력하면 이 단계를 건너뛸 수 있어요
                                  </span>
                                )}
                              </>
                            )
                            : questionDetail.questionContent || ''}
                        </p>
                        {/* 참고 링크 — 본문 안 노출 (M-022) */}
                        {surveyStep === null && questionDetail.referenceType && (
                          <button
                            type="button"
                            onClick={() => {
                              if (questionDetail.referenceType === 'SURVEY' && questionDetail.referenceQuestionId) {
                                navigate(`/question/info/${questionDetail.referenceQuestionId}`);
                              } else if (questionDetail.referenceType === 'EXTERNAL' && questionDetail.referenceUrl) {
                                window.open(questionDetail.referenceUrl, '_blank', 'noopener,noreferrer');
                              }
                            }}
                            className="mt-2 inline-flex items-center rounded-lg bg-blue-50 px-2.5 py-1 text-xs font-medium text-blue-600 transition-colors hover:bg-blue-100">
                            {questionDetail.referenceType === 'EXTERNAL'
                              ? '관련 기사 읽기'
                              : (questionDetail.referenceLabel ?? '이 설문도 같이 봐봐!')}
                          </button>
                        )}
                      </div>
                    </div>
                  </div>
                </div>
              </CardHeader>
              <CardContent>
                {/* 연계설문 활성 시 */}
                {surveyStep !== null ? (
                  <div className="animate-fade-in">
                    {surveyStep === 'gender' && (
                      <div className="space-y-3">
                        {[
                          { label: '남성', value: 'MALE' },
                          { label: '여성', value: 'FEMALE' },
                          { label: '입력안함', value: null },
                        ].map((option) => (
                          <Button
                            key={option.label}
                            variant="outline"
                            className="w-full p-4 h-auto justify-start hover:bg-accent active:scale-[0.98] transition-all"
                            onClick={() => handleSurveyGender(option.value)}>
                            <span className={option.value === null ? 'text-muted-foreground' : ''}>
                              {option.label}
                            </span>
                          </Button>
                        ))}
                      </div>
                    )}
                    {surveyStep === 'ageGroup' && (
                      <div className="space-y-3">
                        {[
                          { label: '10대', value: 'TEENS' },
                          { label: '20대', value: 'TWENTIES' },
                          { label: '30대', value: 'THIRTIES' },
                          { label: '40대', value: 'FORTIES' },
                          { label: '50대', value: 'FIFTIES' },
                          { label: '60대 이상', value: 'SIXTY_PLUS' },
                          { label: '입력안함', value: null },
                        ].map((option) => (
                          <Button
                            key={option.label}
                            variant="outline"
                            className="w-full p-4 h-auto justify-start hover:bg-accent active:scale-[0.98] transition-all"
                            onClick={() => handleSurveyAgeGroup(option.value)}>
                            <span className={option.value === null ? 'text-muted-foreground' : ''}>
                              {option.label}
                            </span>
                          </Button>
                        ))}
                      </div>
                    )}
                  </div>
                ) : (
                  /* 기본 투표 옵션 영역 */
                  <>
                    <div className="space-y-4">
                      {questionDetail.questionOptions.map((option, index) => {
                        const isSelected = questionDetail.hasVoted && questionDetail.userVotedOrder! === option.optionOrder;
                        let variant: 'default' | 'outline' | 'secondary';
                        const canVote = !questionDetail.hasVoted && !questionDetail.isExpired;
                        if (questionDetail.hasVoted) {
                          variant = isSelected ? 'default' : 'secondary';
                        } else {
                          variant = 'outline';
                        }
                        return (
                          <Button
                            key={option.id}
                            disabled={!canVote}
                            variant={variant}
                            className={`w-full p-4 h-auto text-left relative overflow-hidden justify-start ${canVote ? 'hover:bg-accent cursor-pointer active:scale-[0.98] transition-transform' : 'cursor-default'} ${questionDetail.isExpired ? 'opacity-75' : ''}`}
                            onClick={() => handleVote(option.id, option.optionOrder)}>
                            {/* 배경 진행률 표시 */}
                            {(questionDetail.hasVoted || questionDetail.isExpired) && (
                              <div
                                className="absolute inset-0 bg-primary/10 transition-all duration-500"
                                style={{
                                  width: `${Math.round((option.voteCount / totalVotes) * 100)}%`,
                                }}
                              />
                            )}
                            <div className="relative w-full">
                              <div className="flex items-start justify-between w-full mb-2">
                                <span className="flex-1">{option.optionText}</span>
                                {(questionDetail.hasVoted || questionDetail.isExpired) && (
                                  <div className="flex items-center gap-3 text-base flex-shrink-0">
                                    <span className="text-muted-foreground">{option.voteCount}명</span>
                                    <span className="font-medium">
                                      {Math.round((option.voteCount / totalVotes) * 100)}%
                                    </span>
                                  </div>
                                )}
                              </div>
                              {option.optionTextDetail && <p>{option.optionTextDetail}</p>}
                            </div>
                          </Button>
                        );
                      })}
                    </div>
                    {/* 투표 완료 안내 */}
                    {questionDetail.hasVoted && !questionDetail.isExpired && (
                      <div className="mt-6 p-4 bg-green-50 rounded-lg border border-green-200">
                        <div className="flex items-center gap-3 text-base text-green-700">
                          <span>✅</span>
                          <span className="text-sm">투표가 완료되었습니다. 결과를 확인해보세요!</span>
                        </div>
                      </div>
                    )}
                    {/* 종료된 설문 안내 */}
                    {questionDetail.isExpired && (
                      <div className="mt-6 p-4 bg-gray-50 rounded-lg border border-gray-200">
                        <div className="flex items-center gap-3 text-base text-gray-600">
                          <span>⏰</span>
                          <span className="text-sm">이 설문이 종료되어 더 이상 투표할 수 없습니다.</span>
                        </div>
                      </div>
                    )}
                    {/* 설문 분석 섹션 (S6-23 고도화) */}
                    {canShowAnalysisArea && (
                      !showAnalysis ? (
                        <div className="mt-6 p-4 bg-blue-50 rounded-lg border border-blue-200">
                          <div className="flex flex-col items-center gap-3">
                            <Button
                              variant="default"
                              className="w-full"
                              disabled={isUnlocking}
                              onClick={handleAnalysisClick}>
                              <BarChartIcon className="w-4 h-4 mr-2" />
                              {isUnlocking ? '해제 중...' : canViewFree ? '분석 보기'
                                : (policy === 'FIRST_FREE_THEN_AD' ? '광고보고 분석 다시보기' : '광고보고 설문분석보기')}
                            </Button>
                            {!canViewFree && (
                              <p className="text-xs text-blue-600">
                                {policy === 'FIRST_FREE_THEN_AD'
                                  ? '광고를 보면 분석을 다시 볼 수 있어요'
                                  : '광고를 보면 30분간 광고가 제거돼요'}
                              </p>
                            )}
                          </div>
                        </div>
                      ) : (
                        analysisData && (
                          <VoteAnalysisSection
                            data={analysisData}
                            profile={votingProfileData}
                            aiAnalysis={aiData}
                            isAiLoading={isAiLoading}
                            isAuthenticated={isAuthenticated}
                          />
                        )
                      )
                    )}
                  </>
                )}
              </CardContent>

              {surveyStep === null && (
                <CardFooter className="flex-col items-stretch space-y-6 pt-0">
                  {/* 좋아요/싫어요 반응 */}
                  {questionId && (
                    <UserReactions
                      questionId={questionId}
                      onClickReaction={handleReaction}
                      questionReaction={questionDetail.questionReaction}
                    />
                  )}
                  {/* 투표 정보 및 결과 */}
                  <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <div className="flex items-center gap-3 text-base text-muted-foreground">
                      <BarChartIcon className="w-5 h-5" />
                      <span>{totalVotes}명 참여</span>
                      {questionDetail.isExpired && (
                        <div>
                          <span>•</span>
                          <span className="text-gray-500">설문 종료</span>
                        </div>
                      )}
                    </div>
                  </div>
                  {/* 광고 미니배너 */}
                  {detailAd && (
                    <MiniBanner ad={detailAd} position="DETAIL_COMMENT" />
                  )}
                  {/* 댓글 섹션 */}
                  <CommentSection questionId={questionDetail.questionId} isAuthenticated={isAuthenticated} user={user!} />
                </CardFooter>
              )}
            </Card>
          </SectionLoading>
        </div>
      </div>
      {/* 설문 삭제 확인 모달 */}
      {isDeleteModalOpen && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl max-w-sm w-full p-6">
            <h3 className="text-lg font-semibold mb-2">질문을 삭제하시겠습니까?</h3>
            <p className="text-sm text-red-500 mb-6 leading-relaxed">
              삭제한 질문은 복구할 수 없습니다. 모든 투표와 댓글도 함께 삭제됩니다.
            </p>
            <div className="flex flex-col sm:flex-row gap-3">
              <Button variant="outline" onClick={() => setIsDeleteModalOpen(false)}>
                취소
              </Button>
              <Button variant="destructive" onClick={handleDeleteQuestion}>
                삭제
              </Button>
            </div>
          </div>
        </div>
      )}
      {/* 모달 */}
      <LoginRequireModal isOpenLoginModal={isLoginRequireOpen} onCloseLoginRequireModal={onCloseLoginRequireModal} />
      {/*<QuestionResultModal />*/}
      <ReportModal
        isOpenReportModal={isReportOpen}
        isSubmitting={isReportSubmitting}
        isSubmitted={isReportSubmitted}
        onCloseReportModal={onCloseReportModal}
        questionTitle={questionDetail.questionTitle}
        createReportParam={createReportParam}
        onParamChange={handleReportParamChange}
        handleCreateReport={handleCreateReport}
      />
    </div>
  );
};

export default QuestionDetail;
